<template>
  <section v-if="featureEnabled && category" class="jcdp" data-testid="job-category-detail-page">
    <!-- ── Breadcrumb ── -->
    <ExBreadcrumbs
      variant="back"
      :breadcrumbsList="breadcrumbsList"
      data-testid="detail-breadcrumb"
      @onClickBreadcrumb="onBack"
    />

    <!-- ── Identity / status header (shared OrgObjectHeader, ER-11883) ── -->
    <header class="jcdp-header">
      <OrgObjectHeader
        type="JobCategory"
        :status="header.status"
        :archived="Boolean(category.archived)"
        :code="header.code"
        :name="header.name"
        :menuItems="moreMenuItems"
        :chatterUnreadCount="header.unread"
        :readiness="readinessPercent"
        :last_month_readiness="lastMonthReadiness"
        data-testid="category-header"
        @chatterClick="onChatter"
        @optionClick="onHeaderOption"
      >
        <template v-if="isEditMode && !renameViaDialog" #name>
          <div class="jcdp-header__edit-name">
            <label
              class="jcdp-header__label jcdp-sr-only"
              for="jcdp-name-input"
              data-testid="category-name-label"
            >
              Job category name
            </label>
            <input
              id="jcdp-name-input"
              v-model="draftName"
              class="jcdp-header__input"
              type="text"
              autocomplete="off"
              placeholder="e.g. Management"
              data-testid="category-name-input"
            />
            <span v-if="header.code" class="jcdp-header__code" data-testid="category-code">
              {{ header.code }}
            </span>
          </div>
        </template>
      </OrgObjectHeader>
      <p class="jcdp-header__subtitle">{{ header.subtitle }}</p>
    </header>

    <!-- ── Meta row + footer (Cancel/Save or Edit job category) ── -->
    <div class="jcdp-meta-row">
      <div class="jcdp-meta">
        <span class="jcdp-meta__positions" data-testid="positions-count">
          <svg
            class="jcdp-meta__positions-icon"
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            aria-hidden="true"
          >
            <circle cx="5.5" cy="4.75" r="2" stroke="currentColor" stroke-width="1.25" />
            <circle cx="11" cy="5.5" r="1.5" stroke="currentColor" stroke-width="1.25" />
            <path
              d="M1.75 13.25c0-2.07 1.68-3.5 3.75-3.5s3.75 1.43 3.75 3.5"
              stroke="currentColor"
              stroke-width="1.25"
              stroke-linecap="round"
            />
            <path
              d="M9.75 9.75c1.93.18 3.25 1.66 3.25 3.5"
              stroke="currentColor"
              stroke-width="1.25"
              stroke-linecap="round"
            />
          </svg>
          <span class="jcdp-meta__positions-label">{{ header.positionLabel }}</span>
        </span>
        <span v-if="header.updatedLabel" class="jcdp-meta__updated" data-testid="updated-label">
          Updated {{ header.updatedLabel }}
        </span>
      </div>

      <div class="jcdp-footer">
        <template v-if="isEditMode">
          <button
            type="button"
            class="jcdp-btn jcdp-btn--secondary"
            data-testid="cancel-changes"
            @click="onCancel"
          >
            Cancel
          </button>
          <button
            type="button"
            class="jcdp-btn jcdp-btn--primary"
            :class="{ 'jcdp-btn--disabled': !canSave }"
            :disabled="!canSave"
            data-testid="save-changes"
            @click="onSave"
          >
            Save changes
          </button>
        </template>
        <template v-else>
          <button
            type="button"
            class="jcdp-btn jcdp-btn--primary"
            data-testid="edit-category"
            @click="onEdit"
          >
            Edit job category
          </button>
        </template>
      </div>
    </div>

    <p
      v-if="isEditMode && saveError"
      class="jcdp-error"
      role="alert"
      data-testid="save-error"
    >
      {{ saveError }}
    </p>

    <!-- ── 4-Factor Score summary + overall Total Score ── -->
    <div class="jcdp-summary" data-testid="score-summary">
      <div class="jcdp-summary__head">
        <h2 class="jcdp-summary__title">4-Factor Score</h2>
        <span class="jcdp-summary__divider" aria-hidden="true" />
        <div class="jcdp-summary__total">
          <div class="jcdp-summary__total-number">
            <strong
              :class="{ 'jcdp-summary__total-value--empty': totalScore.total === null }"
              data-testid="total-score"
              >{{ totalScore.total === null ? "Not scored" : totalScore.total }}</strong
            >
            <span v-if="totalScore.total !== null" class="jcdp-summary__total-denominator"
              >/ 73</span
            >
          </div>
          <div class="jcdp-summary__total-caption">
            <strong class="jcdp-summary__total-caption-title">Total Score</strong>
            <span class="jcdp-summary__total-caption-sub">
              Work-of-equal-value comparator (EIGE 4-factor method)
            </span>
            <span v-if="bandRange" class="jcdp-summary__band-chip" data-testid="value-band-chip">
              <span class="jcdp-summary__band-chip-label">Value band</span>
              <strong class="jcdp-summary__band-chip-range" data-testid="value-band-range">{{
                bandRange
              }}</strong>
            </span>
          </div>
        </div>
      </div>

      <div class="jcdp-summary__gauges">
        <div
          v-for="gauge in summaryGauges"
          :key="gauge.key"
          class="jcdp-summary__gauge"
          :data-testid="`summary-gauge-${gauge.key}`"
        >
          <div class="jcdp-summary__gauge-head">
            <EXTooltip
              :title="gauge.fullLabel"
              :description="gauge.definition"
              placement="top"
              :delay="200"
            >
              <span class="jcdp-summary__gauge-label" :data-testid="`summary-gauge-label-${gauge.key}`"
                >{{ gauge.letter }} · {{ gauge.label }}</span
              >
            </EXTooltip>
            <span class="jcdp-summary__gauge-value">{{ gauge.display }}</span>
          </div>
          <div
            class="jcdp-gauge"
            role="img"
            :aria-label="`${gauge.label} ${gauge.display}`"
          >
            <span class="jcdp-gauge__track" aria-hidden="true">
              <span
                class="jcdp-gauge__fill"
                :class="{ 'jcdp-gauge__fill--muted': !gauge.complete }"
                :style="{ width: gaugeFillPercent(gauge.value) + '%' }"
              />
            </span>
            <span
              class="jcdp-gauge__thumb"
              :style="{ left: gaugeFillPercent(gauge.value) + '%' }"
              aria-hidden="true"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- ── Subfactor sections: Skills / Responsibility / Effort / Working Conditions ── -->
    <div class="jcdp-sections">
      <section
        v-for="section in sections"
        :key="section.key"
        class="jcdp-section"
        :data-testid="`factor-section-${section.key}`"
      >
        <header class="jcdp-section__head">
          <h3 class="jcdp-section__title">{{ section.label }}</h3>
          <span
            class="jcdp-section__total"
            :class="{ 'jcdp-section__total--empty': !section.complete }"
            :data-testid="`factor-total-${section.key}`"
          >
            {{ section.display }}
          </span>
        </header>

        <div class="jcdp-subfactors">
          <div
            v-for="subfactor in section.subfactors"
            :key="subfactor.key"
            class="jcdp-subfactor"
            :class="{ 'jcdp-subfactor--scored': isScored(subfactor.score, subfactor.maxLevel) }"
            :data-testid="`subfactor-${subfactor.key}`"
          >
            <div class="jcdp-subfactor__head">
              <span :id="subfactorLabelId(subfactor.key)" class="jcdp-subfactor__name">
                {{ subfactor.label }}
              </span>
              <span
                class="jcdp-subfactor__badge"
                :class="{ 'jcdp-subfactor__badge--empty': !isScored(subfactor.score, subfactor.maxLevel) }"
                data-testid="subfactor-score"
              >
                {{ scoreBadge(subfactor.score, subfactor.maxLevel) }}
              </span>
            </div>

            <div
              class="jcdp-slider"
              :class="{ 'jcdp-slider--interactive': isEditMode }"
              :aria-labelledby="subfactorLabelId(subfactor.key)"
            >
              <template v-if="isEditMode">
                <div class="jcdp-slider__rail" aria-hidden="true" />
                <div class="jcdp-slider__fill-zone" aria-hidden="true">
                  <span
                    class="jcdp-slider__fill"
                    :class="{ 'jcdp-slider__fill--active': isScored(subfactor.score, subfactor.maxLevel) }"
                    :style="{ width: sliderFillPercent(subfactor.score, subfactor.maxLevel) + '%' }"
                  />
                </div>
                <div class="jcdp-slider__stops">
                  <EXTooltip
                    v-for="tip in subfactor.levelTooltips"
                    :key="tip.level"
                    placement="top"
                    :title="tip.title"
                    :description="tip.description"
                    :delay="0"
                    show-on-child-focus
                  >
                    <button
                      type="button"
                      class="jcdp-slider__stop"
                      :class="{ 'jcdp-slider__stop--active': subfactor.score === tip.level }"
                      :aria-pressed="String(subfactor.score === tip.level)"
                      :aria-label="`${subfactor.label}: level ${tip.level} of ${subfactor.maxLevel}`"
                      :data-testid="`slider-${subfactor.key}-${tip.level}`"
                      @click="onScore(section.key, subfactor.key, tip.level)"
                    />
                  </EXTooltip>
                </div>
                <div class="jcdp-slider__scale" aria-hidden="true">
                  <span
                    v-for="level in subfactor.scoreLevels"
                    :key="level"
                    class="jcdp-slider__scale-label"
                    :class="{ 'jcdp-slider__scale-label--zero': level === 0 }"
                  >
                    {{ scaleLabel(level) }}
                  </span>
                </div>
              </template>
              <template v-else>
                <span class="jcdp-slider__track" aria-hidden="true">
                  <span
                    class="jcdp-slider__fill"
                    :class="{ 'jcdp-slider__fill--active': isScored(subfactor.score, subfactor.maxLevel) }"
                    :style="{ width: sliderFillPercent(subfactor.score, subfactor.maxLevel) + '%' }"
                  />
                </span>
                <span
                  class="jcdp-slider__thumb"
                  :style="{ left: sliderFillPercent(subfactor.score, subfactor.maxLevel) + '%' }"
                  aria-hidden="true"
                />
              </template>
            </div>

            <div class="jcdp-subfactor__meta">
              <p class="jcdp-subfactor__description">{{ subfactor.description }}</p>
              <p
                v-if="subfactor.levelDescription"
                class="jcdp-subfactor__level-description"
                data-testid="subfactor-level-description"
              >
                {{ subfactor.levelDescription }}
              </p>
              <p
                v-else
                class="jcdp-subfactor__level"
                :class="{ 'jcdp-subfactor__level--empty': !isEditMode }"
                data-testid="subfactor-level"
              >
                {{ isEditMode ? subfactor.levelText : "Not scored" }}
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  </section>
</template>

<script setup>
import { computed, getCurrentInstance, onMounted, ref, watch } from "vue";

import OrgObjectHeader from "@components/OrgObjectHeader/OrgObjectHeader.vue";
import ExBreadcrumbs from "@components/breadcrumbs/ExBreadcrumbs.vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import { LD_FLAG_IDEA_MDP_566 } from "@components/constants/launchDarklyFlagKeys";
import { RENAME_MENU_ITEM, rowActionsFor } from "@components/JobCategoriesList/jobCategoriesListRow.js";
import {
  applyDraftSubfactorScore,
  buildSavePayload,
  categoryFactorScoresOf,
  categoryHeaderView,
  categoryName,
  editIsReady,
  factorSections,
  factorSummaryGauges,
  initialDraftFactorScores,
  isScored,
  resolvedCategoryScores,
  overallTotalScore,
  scoreBadge,
  sliderFillPercent,
} from "./jobCategoryDetailPage.js";

/**
 * ER-11381 — JobCategoryDetailPage (Organization > Job Categories).
 *
 * Full-page drill-down for a single Job Category, reached by opening a row on
 * JobCategoriesList. Rebuilt to match Figma node 47317:204060 ("JobCategoryCard")
 * — the identity/status header (the shared OrgObjectHeader, type "JobCategory",
 * ER-11883; in inline-edit mode the name becomes an input in its `name` slot),
 * the subtitle, the positions/updated meta row,
 * the 4-Factor Score summary with an overall Total Score, and the four EIGE
 * subfactor sections (Skills, Responsibility, Effort, Working Conditions) —
 * each subfactor carries a 1–5 slider and its official level descriptor.
 *
 * Controlled presentational component in the style of JobCategoriesList and
 * AddJobCategoryWizard: the parent owns data fetching/persistence and passes
 * the loaded `category` plus the current `mode` ("view" | "edit"). The page
 * keeps an internal draft while `mode === "edit"` (initialised from the
 * category, reset by Cancel) and emits `save` with the draft payload, plus
 * `cancel` / `edit` / `chatter` / `more` / `back` for the parent to act on.
 * `more` now carries the selected status-aware action as `{ id, action }`
 * (reusing `rowActionsFor()` so the detail page mirrors the list row menu).
 * No API calls inside the component.
 *
 * Shipped behind the linked idea flag (idea-MDP-566, "Job Category — Equal-
 * Value Grouping for Fair Pay (EU Directive Alignment)"). Kill-switch
 * semantics: renders by default; set the flag to false to hide the page.
 */
const props = defineProps({
  /** The loaded job category: `{ id, code, name, status, unread, positionCount, updatedLabel, scores, bandRange }`. */
  category: {
    type: Object,
    default: null,
  },
  /** Render state: "view" (read-only) or "edit" (name + subfactor scores + Cancel/Save). */
  mode: {
    type: String,
    default: "view",
    validator: (value) => value === "view" || value === "edit",
  },
  /**
   * ER-11901 — rename through the standard Rename dialog (header menu) instead of
   * the inline name input. The parent turns this on behind idea-MDP-608.
   */
  renameViaDialog: {
    type: Boolean,
    default: false,
  },
  /** `{ percent, changePercent }` for the inline readiness chip after the status badge
   *  (behind idea-MDP-450). `null` = no readiness data, no chip. */
  readiness: {
    type: Object,
    default: null,
  },
  /** Inline message for a rejected save (e.g. a backend 400 on the scores); "" hides it. */
  saveError: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["chatter", "cancel", "save", "edit", "more", "back"]);

/**
 * ER-11746 — the hand-rolled back button is now the shared ExBreadcrumbs
 * component. `href` targets the Job Categories list route so the component's
 * built-in `$router.push` navigation fires once the detail route (ER-11744)
 * ships; until then the `onClickBreadcrumb` → `back` emit resets the in-tab
 * detail state. See components/breadcrumbs/ExBreadcrumbs.vue.
 */
const breadcrumbsList = [
  { text: "Job Categories", href: "/organization/categories" },
  { text: "Job Category", disabled: true },
];

const app = getCurrentInstance();
const { proxy } = app || {};
const featureEnabled = ref(true);

onMounted(async () => {
  try {
    featureEnabled.value = (await proxy?.$getFlag?.(LD_FLAG_IDEA_MDP_566, true)) !== false;
  } catch {
    featureEnabled.value = true;
  }
});

const readinessPercent = computed(() => props.readiness?.percent ?? null);

const lastMonthReadiness = computed(() => {
  if (props.readiness?.percent == null || props.readiness.changePercent == null) return null;
  return props.readiness.percent - props.readiness.changePercent;
});

const isEditMode = computed(() => props.mode === "edit");

const normalizedScores = computed(() => resolvedCategoryScores(props.category));

const header = computed(() => categoryHeaderView(props.category));

/**
 * Status-aware overflow actions for the header "..." menu. Reuses the same
 * `rowActionsFor()` builder as the list row menu (ER-11542) so the detail page
 * offers the identical Activate/Reactivate, Retire, Archive, Unarchive,
 * Delete-if-unassigned and Edit actions driven by `{ status, archived }`.
 */
const moreMenuItems = computed(() => {
  const items = rowActionsFor({
    status: header.value.status,
    archived: Boolean(props.category && props.category.archived),
    positionCount: header.value.positionCount,
  });
  return props.renameViaDialog ? [...items, RENAME_MENU_ITEM] : items;
});

/** Drafts are only meaningful while editing; kept in sync when mode/category change. */
const draftName = ref("");
const draftScores = ref({});
const draftFactorScores = ref({ s: null, e: null, r: null, wc: null });

function initDraft() {
  draftName.value = categoryName(props.category);
  draftScores.value = resolvedCategoryScores(props.category);
  draftFactorScores.value = initialDraftFactorScores(props.category);
}

const totalScore = computed(() =>
  overallTotalScore(isEditMode.value ? draftScores.value : normalizedScores.value)
);

/**
 * ER-11886 — the Value Band point range for the saved Total Score, resolved by
 * the backend from the account's `band_count` (Settings — Jobs, Rules 3–4).
 * Rendered verbatim: this page never derives bands itself. Hidden while editing
 * because the draft score is unsaved, so the stored range could disagree with
 * the number on screen.
 */
const bandRange = computed(() => {
  if (isEditMode.value || totalScore.value.total === null) return "";
  const value = props.category && props.category.bandRange;
  return typeof value === "string" ? value.trim() : "";
});

const summaryGauges = computed(() =>
  isEditMode.value
    ? factorSummaryGauges(draftScores.value, draftFactorScores.value)
    : factorSummaryGauges(normalizedScores.value, categoryFactorScoresOf(props.category))
);

onMounted(initDraft);

watch(
  () => props.category,
  () => {
    // Preserve unsaved edits while the user is mid-edit (e.g. a chatter badge
    // refresh); re-baseline whenever the loaded category changes elsewhere.
    if (!isEditMode.value) initDraft();
  },
  { deep: true }
);

watch(
  () => props.mode,
  (mode) => {
    if (mode === "edit") initDraft();
  }
);

const sections = computed(() =>
  factorSections(isEditMode.value ? draftScores.value : normalizedScores.value)
);

const canSave = computed(() => editIsReady(draftName.value, draftScores.value));

function subfactorLabelId(subfactorKey) {
  return `jcdp-sub-${subfactorKey}`;
}

function gaugeFillPercent(value) {
  return value === null || value === undefined ? 0 : value;
}

function scaleLabel(level) {
  return level === 0 ? "0 — Does not apply" : String(level);
}

function onBack() {
  emit("back");
}

function onChatter() {
  emit("chatter", header.value.id);
}

function onHeaderOption(payload) {
  onMoreAction(payload && payload.item);
}

function onMoreAction(item) {
  if (!item || !item.value) return;
  emit("more", { id: header.value.id, action: item.value });
}

function onEdit() {
  emit("edit");
}

function onScore(factorKey, subfactorKey, level) {
  const next = applyDraftSubfactorScore(
    draftScores.value,
    draftFactorScores.value,
    factorKey,
    subfactorKey,
    level
  );
  draftScores.value = next.scores;
  draftFactorScores.value = next.factorScores;
}

function onCancel() {
  initDraft();
  emit("cancel");
}

function onSave() {
  if (!canSave.value) return;
  emit("save", buildSavePayload(props.category, draftName.value, draftScores.value));
}
</script>

<style scoped>
.jcdp {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  padding: 32px;
  box-sizing: border-box;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 12px;
  font-family: Inter, sans-serif;
  color: #101828;
}

.jcdp-btn:focus-visible,
.jcdp-header__input:focus-visible,
.jcdp-slider__stop:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 1px;
}

/* ── Header ── */
.jcdp-header {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* The shared header's muted code is #98a2b3 (2.58:1 on white); keep this page's
   existing WCAG-passing #667185 (4.92:1). */
.jcdp-header ::v-deep .header-object-name .code-text {
  color: #667185;
}

.jcdp-header__edit-name {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.jcdp-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.jcdp-header__label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}

.jcdp-header__code {
  flex-shrink: 0;
  font-size: 20px;
  font-weight: 400;
  line-height: 28px;
  color: #667185;
}

.jcdp-header__input {
  box-sizing: border-box;
  width: 100%;
  min-width: 200px;
  max-width: 360px;
  height: 44px;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-family: inherit;
  font-size: 16px;
  line-height: 24px;
  color: #101828;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.jcdp-header__input::placeholder {
  color: #667085;
}

.jcdp-header__input:focus {
  border-color: #7f56d9;
  box-shadow: 0 0 0 3px rgba(127, 86, 217, 0.2);
}

.jcdp-header__subtitle {
  margin: 0;
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  color: #667185;
}

/* ── Meta row + footer ── */
.jcdp-meta-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.jcdp-meta {
  display: flex;
  align-items: center;
  gap: 24px;
}

.jcdp-meta__positions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #7f56d9;
}

.jcdp-meta__positions-icon {
  flex-shrink: 0;
  color: #333e50;
}

.jcdp-meta__positions-label {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.jcdp-meta__updated {
  font-size: 13px;
  font-weight: 400;
  line-height: 18px;
  color: #667185;
}

.jcdp-error {
  margin: 0;
  font-size: 14px;
  line-height: 20px;
  color: #b42318;
}

.jcdp-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-left: auto;
}

.jcdp-btn {
  padding: 10px 16px;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.jcdp-btn--secondary {
  background: #ffffff;
  border: 1px solid #d0d5dd;
  color: #344054;
}

.jcdp-btn--primary {
  background: #7f56d9;
  border: 1px solid #7f56d9;
  color: #ffffff;
}

.jcdp-btn--disabled,
.jcdp-btn:disabled {
  background: #e0e0e0;
  border-color: #e0e0e0;
  color: #ffffff;
  cursor: not-allowed;
}

/* ── 4-Factor Score summary ── */
.jcdp-summary {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  background: #fafafc;
  border: 1px solid #eaecf0;
  border-radius: 8px;
}

.jcdp-summary__head {
  display: flex;
  align-items: center;
  gap: 20px;
}

.jcdp-summary__title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  color: #101828;
  white-space: nowrap;
}

.jcdp-summary__divider {
  width: 1px;
  height: 32px;
  background: #eaecf0;
  flex-shrink: 0;
}

.jcdp-summary__total {
  display: flex;
  align-items: center;
  gap: 14px;
}

.jcdp-summary__total-number {
  display: flex;
  align-items: baseline;
  gap: 2px;
}

.jcdp-summary__total-number strong {
  font-size: 32px;
  font-weight: 700;
  line-height: 40px;
  color: #6941c6;
  font-variant-numeric: tabular-nums;
}

.jcdp-summary__total-number .jcdp-summary__total-value--empty {
  font-size: 20px;
  font-weight: 600;
  line-height: 28px;
  color: #667185;
}

.jcdp-summary__total-denominator {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #667185;
}

.jcdp-summary__total-caption {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.jcdp-summary__total-caption-title {
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
  color: #101828;
}

.jcdp-summary__total-caption-sub {
  font-size: 11px;
  font-weight: 400;
  line-height: 16px;
  color: #667185;
}

.jcdp-summary__band-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  margin-top: 4px;
  padding: 2px 8px;
  background: #f9fafb;
  border: 1px solid #eaecf0;
  border-radius: 6px;
  font-size: 12px;
  line-height: 16px;
}

.jcdp-summary__band-chip-label {
  font-weight: 400;
  color: #667185;
}

.jcdp-summary__band-chip-range {
  font-weight: 600;
  color: #101828;
  font-variant-numeric: tabular-nums;
}

.jcdp-summary__gauges {
  display: flex;
  align-items: stretch;
  gap: 40px;
  flex-wrap: wrap;
}

.jcdp-summary__gauge {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1 1 140px;
  min-width: 140px;
}

.jcdp-summary__gauge-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}

.jcdp-summary__gauge-label {
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: #667185;
  white-space: nowrap;
}

.jcdp-summary__gauge-value {
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  color: #101828;
  font-variant-numeric: tabular-nums;
}

.jcdp-gauge {
  position: relative;
  height: 8px;
}

.jcdp-gauge__track {
  position: absolute;
  top: 3px;
  left: 0;
  right: 0;
  display: block;
  height: 2px;
  border-radius: 2px;
  background: #e5e7ec;
  overflow: hidden;
}

.jcdp-gauge__fill {
  display: block;
  height: 100%;
  border-radius: 2px;
  background: #344054;
}

.jcdp-gauge__fill--muted {
  background: #98a2b3;
}

.jcdp-gauge__thumb {
  position: absolute;
  top: 50%;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ffffff;
  border: 1.5px solid #344054;
  transform: translate(-50%, -50%);
}

/* ── Sections ── */
.jcdp-sections {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.jcdp-section {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.jcdp-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.jcdp-section__title {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  line-height: 24px;
  color: #101828;
}

.jcdp-section__total {
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #667185;
  font-variant-numeric: tabular-nums;
}

.jcdp-section__total--empty {
  color: #98a2b3;
}

.jcdp-subfactors {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.jcdp-subfactor {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.jcdp-subfactor__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}

.jcdp-subfactor__name {
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  color: #344054;
}

.jcdp-subfactor__badge {
  flex-shrink: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #667185;
  font-variant-numeric: tabular-nums;
}

.jcdp-subfactor__badge--empty {
  color: #98a2b3;
}

.jcdp-slider {
  position: relative;
  height: 20px;
}

.jcdp-slider--interactive {
  height: auto;
}

.jcdp-slider__track {
  position: absolute;
  top: 9px;
  left: 0;
  right: 0;
  display: block;
  height: 2px;
  border-radius: 2px;
  background: #e5e7ec;
  overflow: hidden;
}

.jcdp-slider__rail {
  position: absolute;
  top: 11px;
  right: 10px;
  left: 10px;
  height: 2px;
  background: #eaecf0;
  border-radius: 1px;
}

.jcdp-slider__fill-zone {
  position: absolute;
  top: 11px;
  right: 10px;
  left: 10px;
  height: 2px;
  pointer-events: none;
}

.jcdp-slider__fill {
  display: block;
  height: 100%;
  border-radius: 2px;
  background: #344054;
}

.jcdp-slider--interactive .jcdp-slider__fill {
  height: 2px;
  background: transparent;
}

.jcdp-slider--interactive .jcdp-slider__fill--active {
  background: #7f56d9;
}

.jcdp-slider__thumb {
  position: absolute;
  top: 50%;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ffffff;
  border: 1.5px solid #344054;
  transform: translate(-50%, -50%);
}

.jcdp-slider--interactive .jcdp-slider__stops {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 24px;
}

.jcdp-slider__stop {
  display: block;
  width: 20px;
  height: 20px;
  padding: 0;
  background: #ffffff;
  border: 1.5px solid #d0d5dd;
  border-radius: 50%;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}

.jcdp-slider__stop:hover {
  border-color: #7f56d9;
}

.jcdp-slider__stop--active {
  background: #7f56d9;
  border-color: #7f56d9;
}

.jcdp-slider__scale {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 4px;
  margin-top: 2px;
  padding: 0 4px;
}

.jcdp-slider__scale-label {
  flex: 0 1 auto;
  min-width: 12px;
  font-size: 12px;
  line-height: 16px;
  color: #667085;
  text-align: center;
}

.jcdp-slider__scale-label--zero {
  max-width: 88px;
  text-align: left;
}

.jcdp-subfactor__meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.jcdp-subfactor__description {
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  color: #667185;
}

.jcdp-subfactor__level-description {
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  color: #344054;
}

.jcdp-subfactor__level {
  margin: 0;
  font-size: 12px;
  line-height: 16px;
  color: #344054;
}

.jcdp-subfactor__level--empty {
  color: #98a2b3;
}
</style>
